// components/exam-player/AudioPlayer.jsx
import React, { useEffect, useRef, useState } from "react";
import { Volume2 } from "lucide-react";

export default function AudioPlayer({
  src,
  allowPause = true,
  oneShot = false, // فقط اگر true باشد بعد از اولین پخش کامل، قفل می‌شود
  className = "",
  autoPlaySignal,
}) {
  const audioRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playedOnce, setPlayedOnce] = useState(false); // برای oneShot
  const [volume, setVolume] = useState(1); // 0..1
  const canControl = ready && (!oneShot || !playedOnce);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onLoaded = () => {
      setDuration(el.duration || 0);
      setReady(true);
      el.volume = volume;
    };
    const onTime = () => setCurrent(el.currentTime || 0);
    const onEnd = () => {
      setPlaying(false);
      if (oneShot) {
        setPlayedOnce(true); // بعد از پایان، قفل
      } else {
        el.currentTime = 0;
      }
    };

    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, [oneShot, volume]);

  // اگر فایل عوض شد ریست
  useEffect(() => {
    setReady(false);
    setDuration(0);
    setCurrent(0);
    setPlaying(false);
    setPlayedOnce(false);
  }, [src]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    // تلاش برای پخش؛ چون از روی کلیک آغاز شده، معمولاً مجازه
    el.play().catch(() => {});
  }, [src, autoPlaySignal]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el || !canControl) return;
    if (playing) {
      if (!allowPause) return;
      el.pause();
      setPlaying(false);
    } else {
      el.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    }
  };

  const fmt = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
  };

  const onSeek = (e) => {
    if (!canControl) return;
    const el = audioRef.current;
    if (!el || !duration) return;
    const val = Number(e.target.value);
    const t = (val / 100) * duration;
    el.currentTime = t;
    setCurrent(t);
  };

  const onVolume = (e) => {
    const v = Math.min(1, Math.max(0, Number(e.target.value)));
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  return (
    <div className={`w-full ${className}`}>
      <audio ref={audioRef} src={src || ""} preload="metadata" />
      <div className="rounded-xl bg-white border p-3">
        {/* کنترل‌ها */}
        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            disabled={!canControl}
            className={`px-3 py-1.5 rounded text-white ${
              playing ? "bg-red-600" : "bg-emerald-600"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={playing ? "Pause" : "Play"}
          >
            {playing ? "Pause" : "Play"}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">
              <Volume2 />
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={onVolume}
              className="flex-1"
            />
          </div>

          {/* نوار پیشرفت */}
          <input
            type="range"
            min={0}
            max={100}
            value={duration ? Math.round((current / duration) * 100) : 0}
            onChange={onSeek}
            disabled={!canControl}
            className="flex-1"
          />

          {/* تایم */}
          <div className="text-xs text-gray-600 tabular-nums min-w-[72px] text-right">
            {fmt(current)} / {fmt(duration)}
          </div>
        </div>

        {/* صدا */}

        {/* پیام قفل‌شدن در حالت تک‌بار پخش */}
        {oneShot && playedOnce && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            این فایل یک‌بار پخش شد و دیگر قابل پخش یا جابه‌جایی نیست.
          </div>
        )}
      </div>
    </div>
  );
}
